/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Loading, HelpIcon} from '../../../components'
import {PageTitle} from '../../../core'
import {KTSVG} from '../../../helpers'

import {useDispatch, useSelector} from 'react-redux'
import {purchaseProposalCompanyCreate} from '../../../redux/slices/purchase.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectPurchaseData = (state) => ({
  isRedirect: state.purchase.isRedirect,
  loading: state.purchase.loading
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageUrl = useMemoizedPageUrl(location)
  const dispatch = useDispatch()

  const {isRedirect, loading} = useSelector(selectPurchaseData)

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    Email: Yup.string()
      .email(intl.formatMessage({id: 'REQUIRED.EMAIL'}))
      .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    Phone: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Name: '',
    FullName: '',
    Address: '',
    Email: '',
    Phone: '',
    Note: '',
    ResponsibleInfo: '',
    ProposalCompanyPeoples: [
      {
        NameSurname: '',
        Phone: '',
        Email: '',
        Description: '',
      },
    ],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(purchaseProposalCompanyCreate(values))
    resetForm()
  }, [dispatch])

  const onClickNewItem = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      NameSurname: '',
      Phone: '',
      Email: '',
      Description: '',
    })
  }, [])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.PROPOSALCOMPANY'}),
      path: '/purchase/proposal-company',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.PROPOSALCOMPANY.ADD'})}
      </PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardHeader title={intl.formatMessage({id: 'BC.PROPOSALCOMPANY.ADD'})}>
              <HelpIcon articleId='purchase-proposal' />
            </KTCardHeader>
            <KTCardBody>
              <FormikProvider value={formik}>
                <form className='form' onSubmit={formik.handleSubmit} noValidate>
                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.NAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Name')}
                      />
                      {formik.touched.Name && formik.errors.Name && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.EMAIL'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Email')}
                      />
                      {formik.touched.Email && formik.errors.Email && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Email}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.PHONE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Phone')}
                      />
                      {formik.touched.Phone && formik.errors.Phone && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Phone}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.FULLNAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('FullName')}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.ADDRESS'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        disabled={formik.isSubmitting}
                        rows={4}
                        {...formik.getFieldProps('Address')}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.NOTE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        disabled={formik.isSubmitting}
                        rows={4}
                        {...formik.getFieldProps('Note')}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.RESPONSIBLEINFO'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('ResponsibleInfo')}
                      />
                    </div>
                  </div>

                  <FieldArray
                    name='ProposalCompanyPeoples'
                    render={(arrayHelpers) => (
                      <>
                        <div className='table-responsive'>
                          <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                            <thead>
                              <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                <th>
                                  {intl.formatMessage({
                                    id: 'COMPANY.CREATE.COMPANYPEOPLE.NAMESURNAME',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({id: 'COMPANY.CREATE.COMPANYPEOPLE.PHONE'})}
                                </th>
                                <th>
                                  {intl.formatMessage({id: 'COMPANY.CREATE.COMPANYPEOPLE.EMAIL'})}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'COMPANY.CREATE.COMPANYPEOPLE.DESCRIPTION',
                                  })}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {formik.values.ProposalCompanyPeoples.map((item, index) => (
                                <tr className='border-bottom border-bottom-dashed' key={index}>
                                  <td className='ps-0'>
                                    <input
                                      type='text'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'COMPANY.CREATE.COMPANYPEOPLE.NAMESURNAME',
                                      })}
                                      name={`ProposalCompanyPeoples[${index}].NameSurname`}
                                      value={
                                        formik.values.ProposalCompanyPeoples[index].NameSurname
                                      }
                                      required
                                      onChange={(event) => formik.handleChange(event)}
                                    />
                                  </td>
                                  <td className='ps-0'>
                                    <input
                                      type='text'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'COMPANY.CREATE.COMPANYPEOPLE.PHONE',
                                      })}
                                      name={`ProposalCompanyPeoples[${index}].Phone`}
                                      value={formik.values.ProposalCompanyPeoples[index].Phone}
                                      required
                                      onChange={(event) => formik.handleChange(event)}
                                    />
                                  </td>
                                  <td className='ps-0'>
                                    <input
                                      type='email'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'COMPANY.CREATE.COMPANYPEOPLE.EMAIL',
                                      })}
                                      name={`ProposalCompanyPeoples[${index}].Email`}
                                      value={formik.values.ProposalCompanyPeoples[index].Email}
                                      required
                                      onChange={(event) => formik.handleChange(event)}
                                    />
                                  </td>
                                  <td className='ps-0'>
                                    <input
                                      type='text'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'COMPANY.CREATE.COMPANYPEOPLE.DESCRIPTION',
                                      })}
                                      name={`ProposalCompanyPeoples[${index}].Description`}
                                      value={
                                        formik.values.ProposalCompanyPeoples[index].Description
                                      }
                                      required
                                      onChange={(event) => formik.handleChange(event)}
                                    />
                                  </td>

                                  <td className='pt-5 text-end'>
                                    <div className='d-flex'>
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-icon btn-active-color-primary'
                                        onClick={() => arrayHelpers.remove(index)}
                                      >
                                        <KTSVG
                                          path='/images/icons/duotune/general/gen027.svg'
                                          className='svg-icon-2'
                                        />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <button
                          type='button'
                          className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                          onClick={() => onClickNewItem(arrayHelpers)}
                        >
                          <KTSVG
                            path='/images/icons/duotune/arrows/arr009.svg'
                            className='svg-icon-2'
                          />
                          {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                        </button>
                      </>
                    )}
                  />

                  <div className='row pt-15'>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                      <button
                        className='btn btn-light'
                        onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                      >
                        {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-primary'
                        data-kt-users-modal-action='submit'
                        disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                      >
                        <span className='indicator-label'>
                          {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                        </span>
                        {formik.isSubmitting && (
                          <span className='indicator-progress'>
                            {intl.formatMessage({id: 'PLEASE'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </FormikProvider>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Create}
